import React, { useEffect, useState, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { jwtDecode } from 'jwt-decode';
import useAuth from "../hooks/useAuth";
import AdminHome from "../AdminHomePage/AdminHome"; // Render AdminHome after exchange
import envProperties from "../../config/envProperties";

const MsilCallbackWrapper = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { login, token, role } = useAuth();
  const [ready, setReady] = useState(false); // Wait to render AdminHome
  const exchangeInProgressRef = useRef(false); // Use ref to track exchange without causing re-renders
  /** Prevents login success toast/state updates after unmount (e.g. OAuth exchange completes after logout). */
  const isMountedRef = useRef(true);

  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const msilConfig = envProperties?.msil || {};
  const exchangeUrl =
    msilConfig.exchangeUrl ||
    "https://massbusinesszone.com/api/auth/exchange-token";
  const redirectUrl =
    msilConfig.redirectUrl ||
    "https://massbusinesszone.com/AdminHome/dashboard";
  const sessionUrl =
    msilConfig.sessionUrl ||
    "https://massbusinesszone.com/api/auth/session";
  const clientId = msilConfig.clientId || "42gc3dtl45anvj24c9msbri04k";

  useEffect(() => {
    // CRITICAL FIX: Prevent re-processing if exchange is already in progress
    if (exchangeInProgressRef.current) {
      console.log("[MSIL Callback] Exchange already in progress, skipping");
      return;
    }
    
    const urlParams = new URLSearchParams(location.search);
    const code = urlParams.get("code");

    // Check if already authenticated (for page refresh scenarios)
    const existingToken = localStorage.getItem('msil_id_token') || sessionStorage.getItem('token');
    const existingRole = sessionStorage.getItem('role');
    const msilToken = localStorage.getItem('msil_id_token');
    
    // CRITICAL FIX: Only allow early return if it's an MSIL user (has msil_id_token or MSIL role)
    const isMsilUser = msilToken || (existingRole && (existingRole === "MSIL" || existingRole === "SUPER_ADMIN" || existingRole === "ADMIN"));
    
    // CRITICAL FIX: If we have valid tokens and role, render dashboard immediately
    if (existingToken && existingRole && isMsilUser) {
      if (!code) {
        // No code in URL - already authenticated (page refresh)
        console.log("[MSIL Callback] Already authenticated MSIL user (no code), rendering dashboard");
        setReady(true);
        return;
      }
      
      // Have code but also have tokens - check if code was already processed
      const codeUsed = sessionStorage.getItem("msil_code_used");
      if (codeUsed === code) {
        // Code was already processed successfully - render dashboard
        console.log("[MSIL Callback] Code already processed successfully, rendering dashboard");
        setReady(true);
        return;
      }
    }

    // No code in URL and not authenticated - redirect to login
    if (!code) {
      console.log("[MSIL Callback] No code and not authenticated, redirecting to login");
      navigate("/login");
      return;
    }

    // CRITICAL FIX: Check if code was used BUT tokens are missing (failed exchange)
    const codeUsed = sessionStorage.getItem("msil_code_used");
    if (codeUsed === code && !existingToken) {
      // Code was marked as used but tokens don't exist - previous exchange failed
      console.log("[MSIL Callback] Code was used but tokens missing (failed exchange), clearing and redirecting");
      sessionStorage.removeItem("msil_code_used");
      navigate("/login");

      return;
    }

    const exchangeCodeForToken = async () => {
      // CRITICAL FIX: Mark exchange as in progress IMMEDIATELY to prevent re-runs
      exchangeInProgressRef.current = true;
      
      try {
        // CRITICAL FIX: Clear ALL MASS data before MSIL login to prevent data contamination
        console.log("[MSIL Callback] MSIL login initiated - clearing ALL MASS data from localStorage and sessionStorage");
        
        // Clear MASS sessionStorage data
        const existingRole = sessionStorage.getItem('role');
        if (existingRole && (existingRole === "MASS_ADMIN" || existingRole === "MASS_EXECUTIVE")) {
          sessionStorage.removeItem('role');
          sessionStorage.removeItem('token');
          sessionStorage.removeItem('uid');
          sessionStorage.removeItem('otpVerified');
          console.log("[MSIL Callback] Cleared MASS sessionStorage role:", existingRole);
        }
        
        // CRITICAL FIX: Clear ALL MASS-related localStorage items
        // These are set by MASS user flows and should not persist for MSIL Admin users
        const massLocalStorageKeys = [
          'dealerCode',
          'dealerInfo',
          'empName',
          'forCode',
          'locationCode',
          'multiServiceDealerCode',
          'outletCode'
        ];
        
        massLocalStorageKeys.forEach(key => {
          if (localStorage.getItem(key)) {
            localStorage.removeItem(key);
            console.log(`[MSIL Callback] Cleared MASS localStorage key: ${key}`);
          }
        });
        
        // Also clear any other MASS-related items that might exist
        // Clear all localStorage keys that start with known MASS prefixes
        Object.keys(localStorage).forEach(key => {
          // Keep MSIL-specific keys (msil_id_token, msil_access_token, msil_code_used)
          // Keep fundcheck_rate_limit (shared between MASS and MSIL)
          if (!key.startsWith('msil_') && 
              key !== 'fundcheck_rate_limit' && 
              (massLocalStorageKeys.includes(key) || 
               key.toLowerCase().includes('dealer') || 
               key.toLowerCase().includes('mass') ||
               key.toLowerCase().includes('emp'))) {
            localStorage.removeItem(key);
            console.log(`[MSIL Callback] Cleared additional MASS localStorage key: ${key}`);
          }
        });
        
        console.log("[MSIL Callback] MASS data cleanup completed");

        // Step 1: Exchange code for tokens
        const response = await axios.post(
          exchangeUrl,
          {
            redirectUrl,
            code,
            clientId,
          },
          {
            headers: { "Content-Type": "application/json" },
          }
        );

        if (!isMountedRef.current) return;

        const tokenData = response.data[0];

        if (!tokenData?.IdToken || !tokenData?.AccessToken) {
          throw new Error("Missing ID or access token in response");
        }

        // Store MSIL tokens in localStorage
        localStorage.setItem("msil_id_token", tokenData.IdToken);
        localStorage.setItem("msil_access_token", tokenData.AccessToken);

        // Step 2: Decode ID token to get email
        let email = null;
        try {
          const decoded = jwtDecode(tokenData.IdToken);
          email = (decoded?.email || decoded?.upn || decoded?.preferred_username)?.toLowerCase();
        } catch (decodeError) {
          console.error("Failed to decode ID token:", decodeError);
          throw new Error("Invalid ID token format");
        }

        if (!email) {
          throw new Error("Could not extract email from ID token");
        }

        // Step 3: Fetch session from backend to get role
        try {
          const encodedEmail = encodeURIComponent(email);
          const sessionResponse = await axios.get(`${sessionUrl}/${encodedEmail}`, {
            headers: {
              Authorization: `Bearer ${tokenData.IdToken}`
            }
          });

          if (!isMountedRef.current) return;

          if (sessionResponse?.data?.role) {
            const role = sessionResponse.data.role;
            
            // Step 4: Store authentication state FIRST (before calling login)
            // CRITICAL FIX: Store all data synchronously in storage first
            sessionStorage.setItem('role', role);
            sessionStorage.setItem('email', email);
            sessionStorage.setItem('token', tokenData.IdToken); // Store idToken as token for compatibility
            
            // CRITICAL FIX: Mark code as used ONLY AFTER tokens are successfully stored
            // This prevents the "code already used but no tokens" scenario
            sessionStorage.setItem("msil_code_used", code);
            
            // Step 5: Update useAuth hook state ASYNCHRONOUSLY (don't wait for it)
            // CRITICAL FIX: login() expects a JWT with role claim, but MSIL ID tokens don't have role
            // So we manually set the role in sessionStorage first, then call login
            // Call login() but don't wait - it will sync in the background
            login(tokenData.IdToken);
            
            // Clean up URL by removing code parameter to prevent re-processing
            const cleanUrl = window.location.pathname + (window.location.search.replace(/[?&]code=[^&]*/, '').replace(/[?&]state=[^&]*/, '') || '');
            if (cleanUrl !== window.location.pathname + window.location.search) {
              window.history.replaceState({}, '', cleanUrl);
            }
            
            console.log("[MSIL] Storage set, login complete, setting ready. Role:", role, "Email:", email);
            if (isMountedRef.current) {
              toast.success("MSIL login successful!", { position: "top-center" });
              setReady(true);
            }
          } else {
            throw new Error("No role found in session response");
          }
        } catch (sessionError) {
          console.error("Failed to fetch session:", sessionError);
          // Still allow login but log warning
          // Try to extract role from token if possible, otherwise use default
          try {
            if (!isMountedRef.current) return;
            const decoded = jwtDecode(tokenData.IdToken);
            // Some ID tokens might have role claim, but usually it's in backend session
            const defaultRole = decoded?.role || "MSIL"; // Fallback to MSIL
            sessionStorage.setItem('role', defaultRole);
            sessionStorage.setItem('email', email);
            sessionStorage.setItem('token', tokenData.IdToken);
            
            // CRITICAL FIX: Mark code as used ONLY AFTER tokens are successfully stored
            sessionStorage.setItem("msil_code_used", code);
            
            login(tokenData.IdToken);
            
            // Clean up URL by removing code parameter to prevent re-processing
            const cleanUrl = window.location.pathname + (window.location.search.replace(/[?&]code=[^&]*/, '').replace(/[?&]state=[^&]*/, '') || '');
            if (cleanUrl !== window.location.pathname + window.location.search) {
              window.history.replaceState({}, '', cleanUrl);
            }
            
            console.log("[MSIL] Fallback: Storage set, login complete, setting ready. Role:", defaultRole, "Email:", email);
            if (isMountedRef.current) {
              toast.success("MSIL login successful!", { position: "top-center" });
              setReady(true);
            }
          } catch (fallbackError) {
            console.error("Fallback login also failed:", fallbackError);
            throw sessionError; // Re-throw original error
          }
        }
      } catch (err) {
        console.error(
          "Token Exchange Failed:",
          err.response?.data || err.message
        );
        if (isMountedRef.current) {
          toast.error("MSIL login failed", { position: "top-center" });
        }
        
        // Clear any partial state
        localStorage.removeItem('msil_id_token');
        localStorage.removeItem('msil_access_token');
        sessionStorage.removeItem('msil_code_used');
        
        // CRITICAL FIX: Reset exchange flag on error so user can retry
        exchangeInProgressRef.current = false;
        
        if (isMountedRef.current) {
          navigate("/login");
        }

      }
    };

    exchangeCodeForToken();
    // CRITICAL FIX: Only depend on location.search - don't include login, hasExchanged, etc.
    // login is a stable function reference, hasExchanged is now tracked via ref
  }, [location.search, navigate, exchangeUrl, redirectUrl, sessionUrl, clientId]);


  // Render AdminHome only if authenticated
  return <AdminHome />;
};

export default MsilCallbackWrapper;
